import { PlanetSkill, SkillFilterMode } from '@apeiron/planet'
import { NextImage, noForwardCustomProps } from '@apeiron/temp'
import { Box, styled, Typography } from '@mui/material'
import ColorizedTypography from '@src/components/share/generic/ColorizedTypography'
import SkillPlayback from '@src/components/share/skill/SkillPlayback'
import SkillUpgradeDescription from '@src/components/share/skill/SkillUpgradeDescription'
import { SkillColorizeMapping } from '@src/constants/layout'
import { SkillPlaybackType } from '@src/constants/skill'
import { useElementContext } from '@src/contexts/planet/ElementContext'
import { usePlanetSkillsContext } from '@src/contexts/planet/PlanetSkillsContext'
import { useSkillContext } from '@src/contexts/skill/SkillContext'
import * as R from 'ramda'
import { FC, useCallback, useMemo } from 'react'

const Container = styled(Box)`
  align-items: flex-start;
  display: flex;
  min-height: 60px;
  padding: 8px 0px;
`

const SkillCardContainer = styled(Box)`
  position: relative;
`

const Image = styled(NextImage, noForwardCustomProps(['square']))<ImageProps>`
  object-fit: contain;
  ${props => props.theme.breakpoints.up('res480')} {
    width: 135px;
  }
  ${props => props.theme.breakpoints.down('res480')} {
    width: 70px;
  }
  ${props => (props.square ? 'aspect-ratio: 1;' : 'aspect-ratio: 0.754;')}
`

const Mana = styled(Typography)`
  font-size: 12px;
  font-weight: 400;
  left: 23%;
  position: absolute;
  top: 19%;
  transform: translate(-50%, -50%);
`

const InfoBox = styled(Box)`
  display: flex;
  flex-direction: column;
  ${props => props.theme.breakpoints.up('res1024')} {
    gap: 3px;
    padding: 0px 10px;
  }
  ${props => props.theme.breakpoints.down('res1024')} {
    padding: 0;
  }
`

const ElementName = styled(
  Typography,
  noForwardCustomProps(['color', 'invertColor'])
)<SkillElementProps>`
  color: ${props => props.color || '#333333'};
  font-size: 12px;
  font-weight: 500;
  line-height: 12px;
  --invert-color: ${props => props.invertColor || props.color};
`

const NameBox = styled(Box)`
  align-items: center;
  display: flex;
  gap: 10px;
`

const Name = styled(Typography)`
  color: #ccc;
  font-size: 14px;
  font-weight: 500;
  line-height: 18px;
`

const Description = styled(ColorizedTypography)`
  color: #ccc;
  font-size: 12px;
  font-weight: 300;
  line-height: 18px;
`

const PlanetSkillItem: FC<Props> = (props: Props) => {
  const { className, mode, skill, showImage = true, showElement = true } = props

  const { extractElement } = useElementContext()

  const { getElementReferenceNo } = usePlanetSkillsContext()

  const { showDetail, skillGlossary } = useSkillContext()

  const handleOnCheckGlossary = useCallback(
    (searchKey: string): string | null => {
      return R.propOr(null, R.toLower(searchKey), skillGlossary)
    },
    [skillGlossary]
  )

  const element = useMemo(() => {
    return extractElement(skill.attributes.element)
  }, [extractElement, skill.attributes.element])

  const shortDescription = skill.descriptionShort || skill.description

  const squareShape =
    mode === SkillFilterMode.Binded || mode === SkillFilterMode.Passive

  const { attributes, image, name, skillId } = skill

  return (
    <Container className={className}>
      {!R.isNil(skill.image) && skill.image !== '' && showImage && (
        <SkillCardContainer className='Custom-Image-Box'>
          <Image className='Custom-Image' square={squareShape} src={image} />
          {attributes.mana > 0 && <Mana>{attributes.mana}</Mana>}
        </SkillCardContainer>
      )}
      <InfoBox className='Custom-Info-Box'>
        {element && showElement && (
          <ElementName
            className='Custom-Element-Name'
            color={element?.color}
            invertColor={element?.invertColor}
          >{`${element.name} - ${getElementReferenceNo(skill)}`}</ElementName>
        )}
        {mode !== SkillFilterMode.Binded && (
          <NameBox className='Custom-Name-Box'>
            <Name className='Custom-Name'>{name}</Name>
            {mode === SkillFilterMode.Active && (
              <SkillPlayback
                className='Custom-Skill-Playback'
                name={name}
                skill={skillId}
                type={SkillPlaybackType.Planet}
              />
            )}
          </NameBox>
        )}
        <Description
          className='Custom-Description'
          colorizeMap={SkillColorizeMapping}
          text={showDetail ? skill.description : shortDescription}
          checkDictionary={handleOnCheckGlossary}
        />
        <SkillUpgradeDescription className='Custom-Upgrade' skill={skill} />
      </InfoBox>
    </Container>
  )
}

type SkillElementProps = {
  color?: string
  invertColor?: string
}

type ImageProps = {
  square: boolean
}

type Props = {
  className?: string
  mode: SkillFilterMode
  skill: PlanetSkill
  showImage?: boolean
  showElement?: boolean
}

export default PlanetSkillItem
